import { isBrowser } from '@/services/Utilities/Runtime';
import { ensureArray } from '@/utils/siteUtils';
import type { SearchDetails, TranslatedLabel } from 'bnef';
import createDOMPurify from 'dompurify';
import type { Translate } from 'next-translate';

export const getLabel = (
    { translationKey, displayName }: TranslatedLabel,
    t: Translate,
    fallback: string | null = null
): string | null => {
    if (translationKey) {
        return t(translationKey);
    }
    return displayName || fallback;
};

export const abbreviate = (input: string): string =>
    input
        .split(' ')
        .map((i) => i.charAt(0))
        .join('');

export const sanitiseHtml = (input: string, allowedTags?: string[]): string => {
    let DOMPurify;
    if (isBrowser) {
        DOMPurify = createDOMPurify(window);
    } else {
        const { JSDOM } = require('jsdom');
        DOMPurify = createDOMPurify(new JSDOM('').window);
    }
    let config = {};
    if (allowedTags) {
        config = {
            ALLOWED_TAGS: allowedTags,
        };
    }

    return DOMPurify.sanitize(input, config);
};

export const sanitiseFileName = (input: string) => {
    return input
        .replace(/[^-\w\s\d._~,;[\]()]/gu, '') // Remove invalid characters
        .replace(/[\s{2,}]/gu, ' ') // Normalise whitespace (including ideographic spaces)
        .trim();
};

export const truncate = (x: string, length: number) => {
    // String length counting / slicing is not safe for character larger than 2 bytes in UTF-16
    x = x.trim();
    const initialLength = x.length;
    if (initialLength <= length) {
        return x;
    }

    x = x.substring(0, length);
    let lastBreak = 0;
    for (const breakCharacter of [' ', '，', '。', '、']) {
        const pos = x.lastIndexOf(breakCharacter);
        if (pos > lastBreak) {
            lastBreak = pos;
        }
    }

    let cutLength = lastBreak;
    // when there are no spaces or we cut too far back (< 50%) just revert to a hard-cut at the required length
    if (!lastBreak || lastBreak < length / 2) {
        cutLength = length - 1;
    }
    return x.substring(0, cutLength) + '…';
};

// Sets do not naturally create JSON, so they need to be changed to an array first
export const safeJsonStringify = (input: any) =>
    JSON.stringify(input, (_key, value) => (value instanceof Set ? [...value] : value));

export const getSearchSummary = (details: SearchDetails): string => {
    const MAX_PER_DETAIL = 3;
    return ensureArray(details)
        .map((detail) => {
            if (!detail || !detail.value || !Array.isArray(detail.value) || detail.value.length === 0) {
                return null;
            }

            const total = detail.value.length;
            const values = detail.value.slice(0, MAX_PER_DETAIL);
            let label = `${detail.label ? `${detail.label}: ` : ''}${values.join(', ')}`;
            if (total > MAX_PER_DETAIL) {
                label = `${label} (+${total - MAX_PER_DETAIL})`;
            }
            return label;
        })
        .filter(Boolean)
        .join(' • ');
};
